import {
  DeesElement,
  html,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { previewStyles } from './styles.js';
import '../../00group-button/dees-button/dees-button.js';
import '../dees-image-viewer/component.js';
import '../dees-audio-viewer/component.js';
import '../dees-video-viewer/component.js';
import '../../00group-dataview/dees-dataview-codebox/dees-dataview-codebox.js';
import '../dees-pdf-viewer/component.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import { demoFunc } from './dees-preview.demo.js';

export type TPreviewContentType = 'image' | 'pdf' | 'audio' | 'video' | 'code' | 'text' | 'unknown';

declare global {
  interface HTMLElementTagNameMap {
    'dees-preview': DeesPreview;
  }
}

const EXTENSION_MAP: Record<string, TPreviewContentType> = {
  // Image
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image',
  svg: 'image', bmp: 'image', avif: 'image', ico: 'image',
  // PDF
  pdf: 'pdf',
  // Audio
  mp3: 'audio', wav: 'audio', ogg: 'audio', flac: 'audio', aac: 'audio',
  m4a: 'audio', opus: 'audio', weba: 'audio',
  // Video
  mp4: 'video', webm: 'video', mov: 'video', avi: 'video', mkv: 'video', ogv: 'video',
  // Code
  ts: 'code', js: 'code', jsx: 'code', tsx: 'code', json: 'code',
  html: 'code', css: 'code', scss: 'code', less: 'code',
  py: 'code', java: 'code', go: 'code', rs: 'code',
  yaml: 'code', yml: 'code', xml: 'code', sql: 'code',
  sh: 'code', bash: 'code', zsh: 'code', md: 'code',
  c: 'code', cpp: 'code', h: 'code', hpp: 'code',
  rb: 'code', php: 'code', swift: 'code', kt: 'code',
  // Text
  txt: 'text', log: 'text', csv: 'text', env: 'text',
};

const MIME_PREFIX_MAP: Record<string, TPreviewContentType> = {
  'image/': 'image',
  'audio/': 'audio',
  'video/': 'video',
  'application/pdf': 'pdf',
};

const EXTENSION_LANG_MAP: Record<string, string> = {
  ts: 'typescript', tsx: 'typescript',
  js: 'javascript', jsx: 'javascript',
  json: 'json', html: 'xml', xml: 'xml',
  css: 'css', scss: 'scss', less: 'less',
  py: 'python', java: 'java', go: 'go', rs: 'rust',
  yaml: 'yaml', yml: 'yaml', sql: 'sql',
  sh: 'bash', bash: 'bash', zsh: 'bash',
  c: 'c', cpp: 'cpp', h: 'c', hpp: 'cpp',
  rb: 'ruby', php: 'php', swift: 'swift', kt: 'kotlin',
  md: 'markdown',
};

const TYPE_ICONS: Record<TPreviewContentType, string> = {
  image: 'lucide:Image',
  pdf: 'lucide:FileText',
  audio: 'lucide:Music',
  video: 'lucide:Video',
  code: 'lucide:Code',
  text: 'lucide:FileText',
  unknown: 'lucide:File',
};

@customElement('dees-preview')
export class DeesPreview extends DeesElement {
  public static styles = [themeDefaultStyles, cssManager.defaultStyles, previewStyles];
  public static demo = demoFunc;
  public static demoGroups = ['Media', 'Data View'];

  // Content sources (use one)
  @property()
  accessor url: string = '';

  @property({ attribute: false })
  accessor file: File | undefined = undefined;

  @property()
  accessor base64: string = '';

  @property()
  accessor textContent: string = '';

  // Hints & overrides
  @property()
  accessor contentType: TPreviewContentType | undefined = undefined;

  @property()
  accessor language: string = '';

  @property()
  accessor mimeType: string = '';

  @property()
  accessor filename: string = '';

  // UI
  @property({ type: Boolean })
  accessor showToolbar: boolean = true;

  @property({ type: Boolean })
  accessor showFilename: boolean = true;

  /** Use when the parent already supplies the preview frame. */
  @property({ type: Boolean, reflect: true })
  accessor frameless: boolean = false;

  // Internal
  @state()
  accessor resolvedType: TPreviewContentType = 'unknown';

  @state()
  accessor resolvedSrc: string = '';

  @state()
  accessor resolvedText: string = '';

  @state()
  accessor resolvedLang: string = 'text';

  @state()
  accessor loading: boolean = false;

  @state()
  accessor error: string = '';

  private objectUrl: string = '';
  private sourceRevision = 0;
  private sourceAbort?: AbortController;

  public render(): TemplateResult {
    const displayName = this.filename || this.file?.name || this.getFilenameFromUrl() || '';

    return html`


      <div class="preview-container">
        ${this.showFilename && displayName ? html`
          <div class="header-bar">
            <dees-icon class="header-icon" icon="${TYPE_ICONS[this.resolvedType]}"></dees-icon>
            <span class="header-filename" title=${displayName}>${displayName}</span>
            <span class="header-kind">${{ image: 'Image', pdf: 'PDF', audio: 'Audio', video: 'Video', code: 'Source', text: 'Text', unknown: 'File' }[this.resolvedType]}</span>
          </div>
        ` : ''}

        <div class="content-area" aria-busy=${this.loading}>
          ${this.error ? html`
            <div class="error-container" role="alert">
              <dees-icon icon="lucide:AlertTriangle"></dees-icon>
              <strong>Unable to open this file</strong><span class="error-text">${this.error}</span><dees-button size="sm" type="secondary" @clicked=${this.reload}>Try again</dees-button>
            </div>
          ` : this.loading ? html`
            <div class="loading-container" role="status">
              <dees-icon class="loading-spinner" icon="lucide:LoaderCircle"></dees-icon><span>Opening preview…</span>
            </div>
          ` : this.renderContent()}
        </div>
      </div>
    `;
  }

  private renderContent(): TemplateResult {
    if (!this.url && !this.file && !this.base64 && !this.textContent && !this.filename) {
      return html`<div class="placeholder"><dees-icon icon="lucide:PanelTop"></dees-icon><strong>Select a file to preview</strong><span>Images, documents, audio, video, and source files.</span></div>`;
    }
    switch (this.resolvedType) {
      case 'image':
        return html`
          <dees-image-viewer
            .src=${this.resolvedSrc}
            .filename=${this.filename || this.file?.name || this.getFilenameFromUrl()}
            .showToolbar=${this.showToolbar}
            alt="${this.filename || ''}"
          ></dees-image-viewer>
        `;
      case 'pdf':
        return html`
          <dees-pdf-viewer
            .pdfUrl=${this.resolvedSrc}
            .frameless=${true}
            .showFooter=${!this.showFilename}
            .filename=${this.filename || this.file?.name || ''}
            .showToolbar=${this.showToolbar}
            initialZoom="page-fit"
          ></dees-pdf-viewer>
        `;
      case 'audio':
        return html`
          <dees-audio-viewer
            .src=${this.resolvedSrc}
            .title=${this.showFilename ? '' : this.filename || this.file?.name || ''}
          ></dees-audio-viewer>
        `;
      case 'video':
        return html`
          <dees-video-viewer
            .src=${this.resolvedSrc}
            .frameless=${true}
          ></dees-video-viewer>
        `;
      case 'code':
        return html`
          <dees-dataview-codebox
            .frameless=${true}
            .progLang=${this.resolvedLang}
            .codeToDisplay=${this.resolvedText}
          ></dees-dataview-codebox>
        `;
      case 'text':
        return html`<pre class="text-viewer">${this.resolvedText}</pre>`;
      default:
        return html`
          <div class="placeholder">
            <dees-icon icon="lucide:FileQuestion"></dees-icon>
            <strong>Preview not available</strong><span>This file type can’t be displayed here.</span>
          </div>
        `;
    }
  }

  public async updated(changedProperties: Map<PropertyKey, unknown>): Promise<void> {
    super.updated(changedProperties);

    const relevant = ['url', 'file', 'base64', 'textContent', 'contentType', 'language', 'mimeType', 'filename'];
    const needsResolve = relevant.some((key) => changedProperties.has(key));
    if (needsResolve && this.isConnected) {
      await this.resolveContent();
    }
  }

  public async connectedCallback(): Promise<void> {
    const reconnecting = this.hasUpdated;
    await super.connectedCallback();
    if (this.isConnected && reconnecting) await this.resolveContent();
  }

  public async disconnectedCallback(): Promise<void> {
    this.sourceRevision++;
    this.sourceAbort?.abort();
    this.revokeObjectUrl();
    await super.disconnectedCallback();
  }

  public async reload(): Promise<void> {
    await this.resolveContent();
  }

  private async resolveContent(): Promise<void> {
    const revision = ++this.sourceRevision;
    this.sourceAbort?.abort();
    const abort = this.sourceAbort = new AbortController();
    this.revokeObjectUrl();
    this.error = '';
    this.loading = false;
    this.resolvedSrc = '';
    this.resolvedText = '';
    this.resolvedType = this.detectType();
    this.resolvedLang = this.resolveLanguage();
    const isText = this.resolvedType === 'code' || this.resolvedType === 'text';
    const current = () => this.isConnected && revision === this.sourceRevision;
    try {
      if (this.url) {
        this.resolvedSrc = this.url;
        if (isText) {
          if (this.textContent) this.resolvedText = this.textContent;
          else {
            this.loading = true;
            const response = await fetch(this.url, { signal: abort.signal });
            if (!response.ok) throw new Error(`Could not load this file (HTTP ${response.status}).`);
            const text = await response.text();
            if (current()) this.resolvedText = text;
          }
        }
      } else if (this.file) {
        if (isText) {
          this.loading = true;
          const text = await this.file.text();
          if (current()) this.resolvedText = text;
        } else {
          this.objectUrl = URL.createObjectURL(this.file);
          this.resolvedSrc = this.objectUrl;
        }
      } else if (this.base64) {
        if (isText) {
          const bytes = Uint8Array.from(atob(this.base64), character => character.charCodeAt(0));
          this.resolvedText = new TextDecoder().decode(bytes);
        } else this.resolvedSrc = `data:${this.mimeType || 'application/octet-stream'};base64,${this.base64}`;
      } else this.resolvedText = this.textContent;
    } catch (error) {
      if (current() && !abort.signal.aborted) this.error = error instanceof Error ? error.message : 'Could not load this file.';
    } finally {
      if (current()) this.loading = false;
    }
  }

  private detectType(): TPreviewContentType {
    // 1. Explicit override
    if (this.contentType) return this.contentType;

    // 2. MIME type
    const mime = (this.mimeType || this.file?.type || '').split(';')[0].trim().toLowerCase();
    if (mime) {
      if (mime === 'application/pdf') return 'pdf';
      for (const [prefix, type] of Object.entries(MIME_PREFIX_MAP)) {
        if (mime.startsWith(prefix)) return type;
      }
      if (['application/json', 'application/javascript', 'application/typescript', 'text/javascript', 'text/typescript', 'text/css', 'application/xml'].includes(mime)) return 'code';
      if (mime.startsWith('text/')) return EXTENSION_MAP[this.getExtension()] === 'code' ? 'code' : 'text';
    }

    // 3. File extension
    const ext = this.getExtension();
    if (ext && EXTENSION_MAP[ext]) return EXTENSION_MAP[ext];

    // 4. If textContent is provided, assume code or text
    if (this.textContent) {
      return this.language ? 'code' : 'text';
    }

    return 'unknown';
  }

  private getExtension(): string {
    const name = this.filename || this.file?.name || '';
    if (name) {
      const parts = name.split('.');
      if (parts.length > 1) return parts.pop()!.toLowerCase();
    }
    if (this.url) {
      try {
        const pathname = new URL(this.url, 'https://placeholder.com').pathname;
        const parts = pathname.split('.');
        if (parts.length > 1) return parts.pop()!.toLowerCase();
      } catch {
        // Invalid URL
      }
    }
    return '';
  }

  private getFilenameFromUrl(): string {
    if (!this.url) return '';
    try {
      const pathname = new URL(this.url, 'https://placeholder.com').pathname;
      return pathname.split('/').pop() || '';
    } catch {
      return '';
    }
  }

  private resolveLanguage(): string {
    if (this.language) return this.language;
    const ext = this.getExtension();
    return EXTENSION_LANG_MAP[ext] || 'text';
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = '';
    }
  }
}
